import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import Accordion from '@mui/material/Accordion';
import { jobRequestDownloadQr } from 'api';
import Box from 'fe-design-base/atoms/Box';
import Icon from 'fe-design-base/atoms/Icon';
import Text from 'fe-design-base/atoms/Text';
import TextInput from 'fe-design-base/atoms/TextInput';
import { InputChangeEvent } from 'fe-design-base/baseTypes';
import AccordionDetails from 'fe-design-base/molecules/Accordions/AccordionDetails';
import AccordionSummary from 'fe-design-base/molecules/Accordions/AccordionSummary';
import Button from 'fe-design-base/molecules/Button';
import IconButton from 'fe-design-base/molecules/IconButton';
import colors from 'fe-design-base/styles/colors';

import { useSendMessage } from 'features/messenger/hooks/useSendMessage';

function ShareOpportunityModal({
  roleName,
  applicationUrl,
  id,
  shareData,
}: {
  roleName: string;
  applicationUrl: string;
  id: string;
  shareData: {
    referral_shared: boolean;
    instagram_shared: boolean;
    sign_downloaded: boolean;
    company_profile_url: string;
    public_job_posting_url: string;
  };
}) {
  const [openStep, setOpenStep] = useState(1);
  const [missingLink, setMissingLink] = useState(false);
  const toDoSteps = new Set([1, 2, 3]);

  const [message, setMessage] = useState(
    `Hi team! We're hiring for a new ${roleName}. ` +
      `If you know someone who fits, please tell them to ` +
      `apply! Details here: ${applicationUrl}`
  );
  const [sendingMessage, setSendingMessage] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { sendToEntireTeam } = useSendMessage();

  const handleSend = useCallback(async () => {
    setSendingMessage(true);
    await sendToEntireTeam(message);
    toDoSteps.delete(2);
    setOpenStep(toDoSteps.entries().next().value[0]);
    setSendingMessage(false);
  }, [message, sendToEntireTeam, toDoSteps]);

  const handleTextEditorChange = useCallback(
    (e: InputChangeEvent) => {
      setMessage(e.target.value);
      if (e.target.value.match(applicationUrl) === null) {
        setMissingLink(true);
      }
    },
    [applicationUrl]
  );

  const addLink = useCallback(() => {
    setMessage(prevState => prevState + ' ' + applicationUrl);
    setMissingLink(false);
  }, [applicationUrl]);

  const handleClickShareOnInstagram = useCallback(() => {
    setOpenStep(1);
  }, []);

  const handleClickMessageTeam = useCallback(() => {
    setOpenStep(2);
  }, []);

  const handleClickGenerateFlyer = useCallback(() => {
    setOpenStep(3);
  }, []);

  useEffect(() => {
    if (shareData.instagram_shared) {
      setOpenStep(2);
    }

    if (shareData.referral_shared) {
      setOpenStep(3);
    }

    if (shareData.sign_downloaded) {
      // All will be closed
      setOpenStep(-1);
    }
  }, [
    shareData.instagram_shared,
    shareData.referral_shared,
    shareData.sign_downloaded,
  ]);

  return (
    <>
      <Box absolute top="40px" right="40px">
        <Button variant="tertiary" size="small" startIcon="Link">
          Copy link
        </Button>
      </Box>
      <Box flex row w="80vw" h="80vh">
        <Box column w="55%" gap={40}>
          <Text variant="heading1">
            Share your job post to get more applicants
          </Text>
          <Box column gap={16}>
            <Accordion expanded={openStep === 1}>
              <AccordionSummary
                onClick={handleClickShareOnInstagram}
                iconName="Complete"
                title="Share on Instagram"
              />
              <AccordionDetails>
                <Box column gap={16}>
                  <Text variant="bodySm" color="mono700">
                    Scan the code so you can share from your phone
                  </Text>
                  <Box>
                    <QRCode
                      value={shareData.public_job_posting_url}
                      size={170}
                    />
                  </Box>
                  <Box
                    flex
                    p={12}
                    bgcolor="mono0"
                    bw={1}
                    bcolor="mono300"
                    bradiusm
                    gap={8}
                    justifyContentSpaceBetween
                  >
                    <Box row gap={8}>
                      <Icon iconName="Info" size="small" color="info300" />
                      <Text variant="bodySm" color="mono700">
                        Add your career page to your Instagram bio
                      </Text>
                    </Box>
                    <Box alignItemsCenter>
                      <Button variant="tertiary" size="small">
                        Copy link
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion expanded={openStep === 2}>
              <AccordionSummary
                onClick={handleClickMessageTeam}
                iconName="Complete"
                title="Ask your team for referrals"
              />
              <AccordionDetails>
                <Box
                  height={156}
                  column
                  relative
                  overflow="hidden"
                  ph={4}
                  pt={4}
                >
                  <TextInput
                    name="team_message"
                    value={message}
                    onChange={handleTextEditorChange}
                    ref={inputRef}
                    textArea
                    minRows={4}
                    maxRows={4}
                    CustomInputStyle={{
                      pb: '48px !important',
                      '&.MuiFormControl-root': {
                        '& .MuiInputBase-root': {
                          pb: '48px !important',
                          '::selection': {
                            backgroundColor: colors.accent100,
                          },
                        },
                      },
                    }}
                  />
                  <Box absolute top={115} right={5}>
                    {missingLink && (
                      <Button
                        variant="tertiary"
                        startIcon="Add"
                        onClick={addLink}
                      >
                        Add job post link
                      </Button>
                    )}
                    <IconButton
                      disabled={sendingMessage}
                      onClick={handleSend}
                      icon="Send"
                    />
                  </Box>
                </Box>
                <Text variant="captions">
                  This message will be sent to everyone on your team
                </Text>
              </AccordionDetails>
            </Accordion>
            <Accordion expanded={openStep === 3}>
              <AccordionSummary
                onClick={handleClickGenerateFlyer}
                iconName="Complete"
                title="Put up a sign in your store for walk ins"
              />
              <AccordionDetails>
                <Button startIcon="Download" href={jobRequestDownloadQr(id)}>
                  Download
                </Button>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
        <Box w="45%" bgcolor="mono100" />
      </Box>
    </>
  );
}

export default ShareOpportunityModal;
